import { LitElement, html, css, svg } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import 'vanilla-colorful/hex-color-picker.js'
import '@material/web/slider/slider.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'

type FillPattern = 'solid' | 'hatch' | 'cross-hatch' | 'dots' | 'diagonal' | 'none'

const FILL_PATTERNS: { pattern: FillPattern; label: string }[] = [
  { pattern: 'solid', label: 'Solid' },
  { pattern: 'hatch', label: 'Hatch' },
  { pattern: 'cross-hatch', label: 'Cross Hatch' },
  { pattern: 'dots', label: 'Dots' },
  { pattern: 'diagonal', label: 'Diagonal' },
  { pattern: 'none', label: 'None' },
]

const PRESET_COLORS = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#84cc16',
  '#22c55e',
  '#10b981',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#d946ef',
  '#ec4899',
  '#f43f5e',
  '#ffffff',
  '#94a3b8',
  '#64748b',
  '#475569',
  '#334155',
  '#1e293b',
  '#000000',
]

@customElement('color-picker-dropdown')
export class ColorPickerDropdown extends LitElement {
  static override styles = css`
    :host {
      position: absolute;
      z-index: 2000;
    }

    .dropdown {
      background: rgba(30, 41, 59, 0.95);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-radius: 12px;
      padding: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      width: 232px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .preset-grid {
      display: grid;
      grid-template-columns: repeat(8, 1fr);
      gap: 4px;
    }

    .preset-swatch {
      width: 24px;
      height: 24px;
      border-radius: 4px;
      border: 2px solid transparent;
      cursor: pointer;
      padding: 0;
      transition:
        border-color 0.15s ease,
        transform 0.1s ease;
    }

    .preset-swatch:hover {
      transform: scale(1.15);
    }

    .preset-swatch.selected {
      border-color: white;
    }

    .preset-swatch[data-color='#ffffff'],
    .preset-swatch[data-color='#000000'] {
      border-color: rgba(255, 255, 255, 0.3);
    }

    .preset-swatch.selected[data-color='#ffffff'],
    .preset-swatch.selected[data-color='#000000'] {
      border-color: white;
    }

    hex-color-picker {
      width: 100%;
      height: 140px;
    }

    hex-color-picker::part(saturation) {
      border-radius: 6px 6px 0 0;
    }

    hex-color-picker::part(hue) {
      border-radius: 0 0 6px 6px;
      height: 14px;
    }

    hex-color-picker::part(saturation-pointer),
    hex-color-picker::part(hue-pointer) {
      width: 18px;
      height: 18px;
    }

    .hex-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .hex-label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 11px;
      font-weight: 500;
    }

    .hex-input {
      flex: 1;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      color: white;
      font-size: 13px;
      font-family: monospace;
      padding: 5px 8px;
      outline: none;
    }

    .hex-input:focus {
      border-color: rgba(255, 255, 255, 0.4);
    }

    .opacity-row {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .opacity-label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 11px;
      font-weight: 500;
      flex-shrink: 0;
    }

    .opacity-value {
      color: white;
      font-size: 12px;
      font-family: monospace;
      min-width: 36px;
      text-align: right;
      flex-shrink: 0;
    }

    .divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.12);
      margin: 2px 0;
    }

    md-slider {
      flex: 1;
      min-width: 0;
      --md-slider-handle-color: white;
      --md-slider-active-track-color: rgba(255, 255, 255, 0.7);
      --md-slider-inactive-track-color: rgba(255, 255, 255, 0.2);
      --md-slider-handle-width: 20px;
      --md-slider-handle-height: 20px;
      --md-slider-active-track-height: 6px;
      --md-slider-inactive-track-height: 6px;
      --md-slider-state-layer-size: 32px;
      --md-slider-hover-handle-color: white;
      --md-slider-pressed-handle-color: white;
      --md-slider-focus-handle-color: white;
      --md-slider-hover-state-layer-color: rgba(255, 255, 255, 0.1);
      --md-slider-pressed-state-layer-color: rgba(255, 255, 255, 0.15);
      --md-slider-label-container-color: rgba(255, 255, 255, 0.9);
      --md-slider-label-text-color: #1e293b;
    }

    md-outlined-select {
      width: 100%;
      --md-outlined-select-text-field-container-height: 40px;
      --md-outlined-select-text-field-container-shape: 8px;
      --md-outlined-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-field-hover-outline-color: rgba(255, 255, 255, 0.5);
      --md-outlined-field-focus-outline-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-input-text-color: white;
      --md-outlined-select-text-field-label-text-color: rgba(255, 255, 255, 0.6);
      --md-outlined-select-text-field-trailing-icon-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-input-text-size: 13px;
      --md-sys-color-surface-container: rgba(30, 41, 59, 0.98);
      --md-sys-color-on-surface: white;
      --md-sys-color-on-surface-variant: rgba(255, 255, 255, 0.7);
      --md-menu-container-color: rgba(30, 41, 59, 0.98);
      --md-menu-container-shape: 8px;
    }

    .option-content {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .pattern-preview {
      flex-shrink: 0;
    }
  `

  @property({ type: String })
  color = '#3b82f6'

  @property({ type: Number })
  opacity = 1

  @property({ type: Boolean })
  showPattern = false

  @property({ type: String })
  pattern: FillPattern = 'solid'

  @state()
  private hexInput = ''

  override connectedCallback(): void {
    super.connectedCallback()
    this.hexInput = this.color
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('color') && !this.shadowRoot?.querySelector('.hex-input:focus')) {
      this.hexInput = this.color
    }
  }

  private handlePickerChange(e: CustomEvent): void {
    const color = e.detail.value
    this.color = color
    this.hexInput = color
    this.dispatchColorChange()
  }

  private handlePresetClick(color: string): void {
    this.color = color
    this.hexInput = color
    this.dispatchColorChange()
    const picker = this.shadowRoot?.querySelector('hex-color-picker') as
      | (HTMLElement & { color: string })
      | null
    if (picker) {
      picker.color = color
    }
  }

  private handleHexInput(e: Event): void {
    const input = e.target as HTMLInputElement
    let val = input.value.trim()
    this.hexInput = val
    if (!val.startsWith('#')) {
      val = '#' + val
    }
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      this.color = val
      this.dispatchColorChange()
      const picker = this.shadowRoot?.querySelector('hex-color-picker') as
        | (HTMLElement & { color: string })
        | null
      if (picker) {
        picker.color = val
      }
    }
  }

  private handleOpacityInput(e: Event): void {
    const slider = e.target as HTMLInputElement
    this.opacity = Number(slider.value) / 100
    this.dispatchColorChange()
  }

  private dispatchColorChange(): void {
    this.dispatchEvent(
      new CustomEvent('color-changed', {
        detail: { color: this.color, opacity: this.opacity },
        bubbles: true,
      })
    )
  }

  private handlePatternSelect(e: Event): void {
    const select = e.target as HTMLSelectElement
    if (select.value) {
      this.pattern = select.value as FillPattern
      this.dispatchEvent(
        new CustomEvent('pattern-changed', {
          detail: { pattern: this.pattern },
          bubbles: true,
        })
      )
    }
  }

  private renderPatternPreview(pattern: FillPattern): unknown {
    const size = 24
    switch (pattern) {
      case 'solid':
        return svg`<svg class="pattern-preview" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" fill="currentColor" opacity="0.5" rx="2"/></svg>`
      case 'hatch':
        return svg`<svg class="pattern-preview" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><defs><pattern id="h" width="6" height="6" patternUnits="userSpaceOnUse"><path d="M0 6L6 0" stroke="currentColor" stroke-width="1.5" opacity="0.7"/></pattern></defs><rect width="${size}" height="${size}" fill="url(#h)" rx="2"/></svg>`
      case 'cross-hatch':
        return svg`<svg class="pattern-preview" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><defs><pattern id="ch" width="6" height="6" patternUnits="userSpaceOnUse"><path d="M0 6L6 0M0 0L6 6" stroke="currentColor" stroke-width="1" opacity="0.7"/></pattern></defs><rect width="${size}" height="${size}" fill="url(#ch)" rx="2"/></svg>`
      case 'dots':
        return svg`<svg class="pattern-preview" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><defs><pattern id="d" width="6" height="6" patternUnits="userSpaceOnUse"><circle cx="3" cy="3" r="1.2" fill="currentColor" opacity="0.7"/></pattern></defs><rect width="${size}" height="${size}" fill="url(#d)" rx="2"/></svg>`
      case 'diagonal':
        return svg`<svg class="pattern-preview" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><defs><pattern id="dg" width="8" height="8" patternUnits="userSpaceOnUse"><path d="M0 8L8 0" stroke="currentColor" stroke-width="2.5" opacity="0.7"/></pattern></defs><rect width="${size}" height="${size}" fill="url(#dg)" rx="2"/></svg>`
      case 'none':
        return svg`<svg class="pattern-preview" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1" stroke-dasharray="3,3" opacity="0.4" rx="2"/></svg>`
    }
  }

  override render(): unknown {
    const opacityPercent = Math.round(this.opacity * 100)

    return html`
      <div class="dropdown">
        ${this.showPattern
          ? html`
              <md-outlined-select
                label="Pattern"
                .value=${this.pattern}
                @change=${this.handlePatternSelect}
              >
                ${FILL_PATTERNS.map(
                  p => html`
                    <md-select-option value=${p.pattern} ?selected=${this.pattern === p.pattern}>
                      <div slot="headline" class="option-content">
                        ${this.renderPatternPreview(p.pattern)}
                        <span>${p.label}</span>
                      </div>
                    </md-select-option>
                  `
                )}
              </md-outlined-select>
              <div class="divider"></div>
            `
          : ''}

        <div class="preset-grid">
          ${PRESET_COLORS.map(
            c => html`
              <button
                class="preset-swatch ${c === this.color ? 'selected' : ''}"
                style="background: ${c}"
                data-color=${c}
                @click=${(): void => this.handlePresetClick(c)}
              ></button>
            `
          )}
        </div>

        <hex-color-picker
          .color=${this.color}
          @color-changed=${this.handlePickerChange}
        ></hex-color-picker>

        <div class="hex-row">
          <span class="hex-label">HEX</span>
          <input
            class="hex-input"
            .value=${this.hexInput}
            @input=${this.handleHexInput}
            maxlength="7"
            placeholder="#000000"
          />
        </div>

        <div class="opacity-row">
          <span class="opacity-label">Opacity</span>
          <md-slider
            min="0"
            max="100"
            .value=${opacityPercent}
            @input=${this.handleOpacityInput}
          ></md-slider>
          <span class="opacity-value">${opacityPercent}%</span>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'color-picker-dropdown': ColorPickerDropdown
  }
}
